import { useState } from 'react';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { Card, Badge, Tabs, TabsList, TabsTrigger, TabsContent } from '@design-system';
import {
  FormInput,
  PrimarySecondaryButtonContainer,
  SubmitButton,
} from '@/components/FormComponents';
import { FormErrorAlert } from '@/components/FormErrorAlert';
import { FeatureFlagsEditor } from '@/components/FeatureFlagsEditor';
import { getErrorMessage } from '../utils/get-error-message';
import {
  useAddPreRegisteredFeatureFlag,
  useAddPreRegisteredFeatureFlagsBulk,
  useSearchPreRegisteredFeatureFlag,
  useDeletePreRegisteredFeatureFlag,
} from '../hooks/use-pre-registered-feature-flags';

const singleEmailSchema = z.object({
  email: z.string().email('A valid email address is required'),
});

const bulkEmailSchema = z.object({
  emails: z.string().min(1, 'At least one email is required'),
});

const searchSchema = z.object({
  email: z.string().min(1, 'Email is required'),
});

function parseFlags(json: string): FeatureFlags {
  try {
    return JSON.parse(json);
  } catch {
    return {};
  }
}

function hasAnyFlagEnabled(json: string): boolean {
  const flags = parseFlags(json);
  return Object.values(flags).some(v => v === true);
}

export function PreRegisteredFeatureFlagsForm() {
  const [activeTab, setActiveTab] = useState('add');

  return (
    <Tabs
      value={activeTab}
      onValueChange={setActiveTab}
    >
      <TabsList className="mb-4">
        <TabsTrigger value="add">Add Email</TabsTrigger>
        <TabsTrigger value="bulk">Bulk Add</TabsTrigger>
        <TabsTrigger value="search">Search & Delete</TabsTrigger>
      </TabsList>

      <TabsContent value="add">
        <AddSingleEmail />
      </TabsContent>

      <TabsContent value="bulk">
        <BulkAddEmails />
      </TabsContent>

      <TabsContent value="search">
        <SearchAndDelete />
      </TabsContent>
    </Tabs>
  );
}

function AddSingleEmail() {
  const [featureFlags, setFeatureFlags] = useState('{}');

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<z.infer<typeof singleEmailSchema>>({
    resolver: zodResolver(singleEmailSchema),
  });

  const { addPreRegisteredFeatureFlag, isPending, isError, error } =
    useAddPreRegisteredFeatureFlag();

  const onSubmit = (data: z.infer<typeof singleEmailSchema>) => {
    addPreRegisteredFeatureFlag(
      { email: data.email, featureFlags: parseFlags(featureFlags) },
      {
        onSuccess: () => {
          reset();
          setFeatureFlags('{}');
        },
      },
    );
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="space-y-4"
    >
      <FormInput
        label="Email Address"
        id="email"
        type="email"
        placeholder="advisor@example.com"
        error={errors.email}
        register={register('email')}
        required
      />

      <div className="space-y-2">
        <h4 className="text-sm font-medium">Feature Flags to Enable on Signup</h4>
        <FeatureFlagsEditor
          value={featureFlags}
          onChange={setFeatureFlags}
        />
      </div>

      <PrimarySecondaryButtonContainer>
        <SubmitButton
          label="Add Email"
          isLoading={isPending}
          disabled={!hasAnyFlagEnabled(featureFlags)}
        />
      </PrimarySecondaryButtonContainer>

      {isError && (
        <FormErrorAlert
          mainErrorMessage={getErrorMessage(error, 'Failed to add email')}
          className="mt-4"
        />
      )}
    </form>
  );
}

function BulkAddEmails() {
  const [featureFlags, setFeatureFlags] = useState('{}');

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<z.infer<typeof bulkEmailSchema>>({
    resolver: zodResolver(bulkEmailSchema),
  });

  const { addBulk, isPending, isError, error } = useAddPreRegisteredFeatureFlagsBulk();

  const onSubmit = (data: z.infer<typeof bulkEmailSchema>) => {
    const emails = data.emails
      .split(',')
      .map(e => e.trim())
      .filter(Boolean);

    if (emails.length === 0) return;

    addBulk(
      { emails, featureFlags: parseFlags(featureFlags) },
      {
        onSuccess: () => {
          reset();
          setFeatureFlags('{}');
        },
      },
    );
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="space-y-4"
    >
      <div className="space-y-1.5">
        <label
          htmlFor="bulk-emails"
          className="text-sm font-medium"
        >
          Email Addresses
        </label>
        <textarea
          id="bulk-emails"
          className="min-h-30 bg-background placeholder:text-muted-foreground focus:ring-primary flex w-full rounded-md border px-3 py-2 text-sm focus:border-transparent focus:outline-none focus:ring-2"
          placeholder="Enter emails separated by commas"
          {...register('emails')}
        />
        {errors.emails && <p className="text-destructive text-xs">{errors.emails.message}</p>}
      </div>

      <div className="space-y-2">
        <h4 className="text-sm font-medium">Feature Flags to Enable on Signup</h4>
        <p className="text-foreground text-xs">
          These flags will be applied to all emails above. Duplicates will be skipped.
        </p>
        <FeatureFlagsEditor
          value={featureFlags}
          onChange={setFeatureFlags}
        />
      </div>

      <PrimarySecondaryButtonContainer>
        <SubmitButton
          label="Add Emails"
          isLoading={isPending}
          disabled={!hasAnyFlagEnabled(featureFlags)}
        />
      </PrimarySecondaryButtonContainer>

      {isError && (
        <FormErrorAlert
          mainErrorMessage={getErrorMessage(error, 'Failed to add emails')}
          className="mt-4"
        />
      )}
    </form>
  );
}

function SearchAndDelete() {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<z.infer<typeof searchSchema>>({
    resolver: zodResolver(searchSchema),
  });

  const {
    search,
    data: searchResult,
    isPending: isSearching,
    isError: isSearchError,
    error: searchError,
    reset: resetSearch,
  } = useSearchPreRegisteredFeatureFlag();
  const { deletePreRegisteredFeatureFlag, isPending: isDeleting } =
    useDeletePreRegisteredFeatureFlag();

  const onSearch = (formData: z.infer<typeof searchSchema>) => {
    search(formData.email.trim());
  };

  const onDelete = () => {
    if (!searchResult?.preRegisteredFeatureFlag) return;
    deletePreRegisteredFeatureFlag(searchResult.preRegisteredFeatureFlag.id, {
      onSuccess: () => resetSearch(),
    });
  };

  return (
    <div className="space-y-4">
      <form
        onSubmit={handleSubmit(onSearch)}
        className="space-y-4"
      >
        <FormInput
          label="Search Email"
          id="search-email"
          type="text"
          placeholder="Enter email to search"
          error={errors.email}
          register={register('email')}
          required
        />

        <PrimarySecondaryButtonContainer>
          <SubmitButton
            label="Search"
            isLoading={isSearching}
          />
        </PrimarySecondaryButtonContainer>

        {isSearchError && (
          <FormErrorAlert
            mainErrorMessage={getErrorMessage(searchError, 'Search failed')}
            className="mt-4"
          />
        )}
      </form>

      {searchResult && !searchResult.found && (
        <Card className="p-4">
          <p className="text-muted-foreground text-sm">No pre-registered email found.</p>
        </Card>
      )}

      {searchResult?.preRegisteredFeatureFlag && (
        <Card className="space-y-3 p-4">
          <h4 className="text-sm font-medium">Pre-Registered Email Found</h4>
          <div className="text-foreground space-y-1 text-sm">
            <p>
              <strong>Email:</strong> {searchResult.preRegisteredFeatureFlag.email}
            </p>
            <p>
              <strong>Flags:</strong>{' '}
              {Object.entries(
                searchResult.preRegisteredFeatureFlag.featureFlags as Record<string, unknown>,
              )
                .filter(([, v]) => v === true)
                .map(([k]) => k)
                .join(', ')}
            </p>
            <p>
              <strong>Added by:</strong> {searchResult.preRegisteredFeatureFlag.createdBy}
            </p>
            <p>
              <strong>Added on:</strong>{' '}
              {new Date(searchResult.preRegisteredFeatureFlag.createdAt).toLocaleDateString()}
            </p>
            <p>
              <strong>Status:</strong>{' '}
              {searchResult.preRegisteredFeatureFlag.consumedAt ? (
                <Badge variant="success">Consumed</Badge>
              ) : (
                <Badge variant="secondary">Pending</Badge>
              )}
            </p>
          </div>
          {!searchResult.preRegisteredFeatureFlag.consumedAt && (
            <PrimarySecondaryButtonContainer>
              <SubmitButton
                label="Delete"
                isLoading={isDeleting}
                onClick={onDelete}
                destructive
              />
            </PrimarySecondaryButtonContainer>
          )}
        </Card>
      )}
    </div>
  );
}
